import { http } from '@/apis/aipass'
import { env } from '@/libs/env'
import { TranslateObject } from '@/libs/translate-text'
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import dayjs, { Dayjs } from 'dayjs'

export const SpacePriceType = {
  Charge: 'charge',
} as const
export type SpacePriceType = (typeof SpacePriceType)[keyof typeof SpacePriceType]
export interface SpaceChargePrice {
  usageTime: {
    start: string | undefined
    end: string | undefined
  }
  unitPrice: number | undefined
  maxPrice: number | undefined
}
export interface SpaceDetail {
  id: string
  title: TranslateObject
  description: TranslateObject
  question: TranslateObject
  images: string[]
  salesHour: {
    start: string
    end: string
  }
  spacePrice:
    | {
        isFreeForGuests: boolean
        type: SpacePriceType
        chargePrice: SpaceChargePrice
      }
    | undefined
  isFixAvailabilityTime: boolean
  availabilityTimes: { [key: string]: number }
  stock: number
  stockUnit: TranslateObject
  usageIntervalTime: number
  isFavorite: boolean
  hasRoomKeySetting: boolean
  isStockByStartTime: boolean
}

export interface ReservationForm {
  spaceId: string
  useDay: string
  quantity: number
  usageFromHour: string
  usageFromMinute: string
  usageToHour: string
  usageToMinute: string
  answer: string
  pax: number
  isFreeForGuests: boolean
  subtotalAmount: number | undefined
  discountAmount: number | undefined
  totalAmount: number | undefined
}

interface getSpaceAvailableTimeParams {
  hotelId: string
  guestAppAssetId: string
  useDay: Dayjs
}
export const getSpaceAvailableTime = createAsyncThunk(
  'space/getSpaceAvailableTime',
  async (params: getSpaceAvailableTimeParams, { rejectWithValue }) => {
    try {
      const response = await http({
        method: 'get',
        url: `${env('BASE_API_V3')}/guest/spaces/available-time`,
        params: {
          ...params,
          hotel_id: params.hotelId,
          useDay: params.useDay.format('YYYY-MM-DD'),
        },
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const getSpaceAvailableTimeByMonth = createAsyncThunk(
  'space/getSpaceAvailableTimeByMonth',
  async (params: getSpaceAvailableTimeParams & { spaceId: string }, { rejectWithValue }) => {
    try {
      const response = await http({
        method: 'get',
        url: `${env('BASE_API_V3')}/guest/spaces/available-time`,
        params: {
          ...params,
          useDay: undefined,
          hotel_id: params.hotelId,
          useMonth: params.useDay.format('YYYY-MM-DD'),
        },
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

const storageKey = 'spacesSlice'
const restoreState = (): {
  spaceDetails: SpaceDetail[]
  spaceDetailsByDay: { [key: string]: SpaceDetail }
  formValues: ReservationForm | undefined
  loading: boolean
} => {
  const sessionData = sessionStorage.getItem(storageKey)
  return sessionData
    ? JSON.parse(sessionData)
    : {
        spaceDetails: [],
        spaceDetailsByDay: {},
        formValues: undefined,
        loading: false,
      }
}

export const timeToMinutes = (time: string | undefined, intervalMinute = 15): number => {
  return time ? Number(time.split(':')[0]) * 60 + Number(time.split(':')[1]) : intervalMinute
}

export const formatTimeWith24 = time => (time === '00:00' ? '24:00' : time)

export const getStartAvailabilityTimes = (space: SpaceDetail, quantity: number): string[] => {
  const { availabilityTimes, usageIntervalTime, salesHour } = space
  const minUsageMinute = timeToMinutes(space.spacePrice?.chargePrice.usageTime.start, usageIntervalTime)

  const availabilityTimeByQuantity = Object.keys(availabilityTimes || {}).filter(time => availabilityTimes[time] >= quantity)
  const todayStartMinute = timeToMinutes(salesHour.start)
  const availabilityTimeByQuantityAndMinUsageMinute = availabilityTimeByQuantity
    .filter(time => {
      // 利用時間間隔でフィルタ
      const currentStartTime = timeToMinutes(time)
      return (currentStartTime - todayStartMinute) % usageIntervalTime === 0
    })
    .filter(time => {
      // 最低利用時間が終了時間を超えているものをフィルタ
      const minUsageEndTime = dayjs(`2024-01-01 ${time}`).add(minUsageMinute, 'minute')
      return !minUsageEndTime.isAfter(dayjs(`2024-01-01 ${salesHour.end}`))
    })
  return availabilityTimeByQuantityAndMinUsageMinute
}

const spacesSlice = createSlice({
  name: 'spaces',
  initialState: restoreState(),
  reducers: {
    storeFormValues: (state, action: PayloadAction<Partial<ReservationForm>>) => {
      const newState = {
        ...state.formValues,
        ...action.payload,
      } as ReservationForm
      state.formValues = newState
      sessionStorage.setItem(storageKey, JSON.stringify(state))
    },
  },
  extraReducers(builder) {
    builder.addCase(getSpaceAvailableTime.fulfilled, (state, action) => {
      state.loading = false
      state.spaceDetails = action.payload
    })
    builder.addCase(getSpaceAvailableTime.rejected, state => {
      state.loading = false
    })
    builder.addCase(getSpaceAvailableTime.pending, state => {
      state.loading = true
    })
    builder.addCase(getSpaceAvailableTimeByMonth.fulfilled, (state, action) => {
      state.loading = false
      state.spaceDetailsByDay = action.payload
    })
    builder.addCase(getSpaceAvailableTimeByMonth.rejected, (state, action) => {
      state.loading = false
    })
    builder.addCase(getSpaceAvailableTimeByMonth.pending, (state, action) => {
      state.loading = true
    })
  },
})
export const { storeFormValues } = spacesSlice.actions
export const spacesReducer = spacesSlice.reducer
